import { useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useDeclineListingChange } from "./use-decline-listing-change";

// Decline dialog for a listing-change request (MDF decline-request-dialog
// template, but the note is OPTIONAL — listingDeclineSchema caps it at 1000
// chars and the portal shows it verbatim when present). On success the
// dialog closes; the drawer stays open and re-renders from the invalidated
// detail query (MDF convention).

export function DeclineListingDialog({
  requestId,
  open,
  onOpenChange,
}: {
  requestId: string;
  open: boolean;
  onOpenChange: (next: boolean) => void;
}) {
  const decline = useDeclineListingChange();
  const [note, setNote] = useState("");

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = note.trim();
    if (trimmed.length > 1000) {
      toast.error("Note is too long (max 1000 characters)");
      return;
    }
    decline.mutate(
      { id: requestId, note: trimmed.length > 0 ? trimmed : undefined },
      {
        onSuccess: () => {
          onOpenChange(false);
          setNote("");
        },
      },
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next);
        if (!next) setNote("");
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">
            Decline change
          </DialogTitle>
          <DialogDescription>
            Add an optional note for the partner — they&apos;ll see it on
            their My Listing page.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-3">
          <div className="grid gap-1.5">
            <Label htmlFor="listing-decline-note">Note (optional)</Label>
            <Textarea
              id="listing-decline-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={4}
              maxLength={1000}
              placeholder="e.g. The new description overstates the integration scope — please tone it down."
            />
            <p className="text-[11px] text-muted-foreground">
              {note.length}/1000
            </p>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={decline.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="destructive"
              disabled={decline.isPending}
            >
              {decline.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Decline
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
